// Local development may use the local Express server. A hosted static preview must never
// attempt to call localhost on the visitor's computer; without an explicit API URL it
// stays in the existing labeled preview/fallback states.
const API_BASE = import.meta.env.VITE_FADIUS_API_URL || (import.meta.env.DEV ? "http://127.0.0.1:3001" : "");

// Session token: held in sessionStorage (cleared when the tab closes). Storage can be unavailable
// (private windows, blocked site data), so every access is guarded and the app still works signed out.
const TOKEN_KEY = "fadius.session";
let memoryToken = null;
export function getAuthToken() {
  try { return sessionStorage.getItem(TOKEN_KEY) || memoryToken; } catch { return memoryToken; }
}
function setAuthToken(token) {
  memoryToken = token || null;
  try { token ? sessionStorage.setItem(TOKEN_KEY, token) : sessionStorage.removeItem(TOKEN_KEY); } catch { /* memory fallback */ }
}

// True on a hosted static preview with no API configured. UI uses this to label features that are not live.
export const PREVIEW_MODE = !API_BASE;
export const PREVIEW_UNAVAILABLE_MESSAGE = "This isn't live in the early preview yet. Nothing was saved.";

async function request(path, options = {}) {
  if (!API_BASE) {
    const error = new Error(PREVIEW_UNAVAILABLE_MESSAGE);
    error.code = "preview_unavailable";
    throw error;
  }
  const token = getAuthToken();
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(options.headers || {}) },
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(body.error || `Watch API request failed (${response.status})`);
    error.status = response.status;
    error.body = body;
    throw error;
  }
  return body;
}

function queryString(params) {
  const entries = Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "");
  return entries.length ? `?${entries.map(([key, value]) => `${key}=${encodeURIComponent(value)}`).join("&")}` : "";
}

// With venueId the server returns only the events eligible at that venue.
export function getLiveEvents({ sport, venueId } = {}) {
  return request(`/api/watch/events${queryString({ sport, venueId })}`);
}

// Nearby venues. dataState "live"/"stale" means real OpenStreetMap venues (no crowd data);
// "illustrative" means the server is still on its mock fixtures.
export function getVenues({ lat, lon, sport, radius } = {}) {
  return request(`/api/watch/venues${queryString({ lat, lon, sport, radius })}`);
}

export function getStadiums() {
  return request("/api/attend/stadiums");
}

// `location` is the RAW reading ({ lat, lon, accuracyMeters, checkedAt }); the server decides whether it counts.
export function submitArrival({ stadiumId, location }) {
  return request("/api/attend/arrivals", { method: "POST", body: JSON.stringify({ stadiumId, location }) });
}

export function getLeaderboard({ scope = "global", venueId, period = "week" } = {}) {
  return request(`/api/watch/leaderboard${queryString({ scope, venueId: scope === "venue" ? venueId : undefined, period })}`);
}

export function getMapConfig() {
  return request("/api/watch/map-config");
}

export function submitCheckIn(payload) {
  return request("/api/watch/check-ins", { method: "POST", body: JSON.stringify(payload) });
}

export function updateCheckIn(checkInId, payload) {
  return request(`/api/watch/check-ins/${encodeURIComponent(checkInId)}`, { method: "PUT", body: JSON.stringify(payload) });
}

export function getVenueDetails(venueId) {
  return request(`/api/watch/venues/${encodeURIComponent(venueId)}`);
}

export function getVenueConfirmation(token) {
  return request(`/api/venue-confirmations/${encodeURIComponent(token)}`);
}

export function submitVenueConfirmation(token, answer) {
  return request(`/api/venue-confirmations/${encodeURIComponent(token)}`, { method: "POST", body: JSON.stringify({ answer }) });
}

export async function deleteAccount() {
  const result = await request("/api/profile", { method: "DELETE", body: JSON.stringify({ confirm: "DELETE" }) });
  setAuthToken(null);
  return result;
}

export function exportMyData() {
  return request("/api/profile/export");
}

export function getProfile() {
  return request("/api/profile");
}

export function getSession() {
  return request("/api/auth/session");
}

// The server revokes the token, so the request must go out (with the Authorization header) BEFORE the
// local token is cleared. The local token is cleared even if the network call fails.
export async function signOut() {
  try {
    return await request("/api/auth/sign-out", { method: "POST" });
  } catch {
    return { authenticated: false, user: null };
  } finally {
    setAuthToken(null);
  }
}

// Exchange a Google ID token (from Google Identity Services) for a Fadius session. Verified server-side.
export async function signInWithGoogle(idToken) {
  const session = await request("/api/auth/google", { method: "POST", body: JSON.stringify({ idToken }) });
  setAuthToken(session.token);
  return session;
}

// Only works when the server runs with AUTH_DEV_MODE=true (never in production).
export async function devLogin(name) {
  const session = await request("/api/auth/dev-login", { method: "POST", body: JSON.stringify({ name }) });
  setAuthToken(session.token);
  return session;
}

export function getSports() {
  return request("/api/watch/sports");
}

export function saveFavorites(favorites) {
  return request("/api/profile/favorites", { method: "PUT", body: JSON.stringify(favorites) });
}

export function getPrivacy() {
  return request("/api/profile/privacy");
}

export function savePrivacy(privacy) {
  return request("/api/profile/privacy", { method: "PUT", body: JSON.stringify(privacy) });
}

export function requestMissingEvent(payload) {
  return request("/api/watch/events/missing", { method: "POST", body: JSON.stringify(payload) });
}
